import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Button, Card } from 'react-bootstrap';
import { getKnockouts, KnockoutMatch, KnockoutRound, KnockoutsResponse } from '../../api/tournament/knockoutsService';
import { getSportValue as getSportValueUtil } from '../../utils/getSportValue';
import { getAllAgeCategoriesPaginated } from '../../api/tournament/ageCategoryService';
import Breadcrumbs from '../../Common/Breadcrumbs';
import ShimmerLoader from '../../Common/ShimmerLoader';
import TournamentHeader from '../../Common/TournamentHeader';
import '../../Common/TournamentHeader.css';
import '../../Common/ManageTournamentsFixturesTable.css';
import './KnockoutsDetailPage.css';

const KnockoutsDetailPage: React.FC = () => {
  const { tournamentId, ageCategoryId } = useParams<{ tournamentId: string; ageCategoryId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [knockoutsData, setKnockoutsData] = useState<{ rounds: KnockoutRound[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ageCategoryInfo, setAgeCategoryInfo] = useState<any>(null);
  const matchRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Normalize round names
  const normalizeRoundName = (round: string): string => {
    if (!round) return 'Unknown Round';
    const roundLower = round.toLowerCase().trim();
    if (roundLower.includes('round_16') || roundLower.includes('round of 16') || roundLower === 'round_16') {
      return 'Round of 16';
    } else if (roundLower.includes('quarter') || roundLower === 'quarter final' || roundLower === 'quarterfinal') {
      return 'Quarter Finals';
    } else if (roundLower.includes('semi') && !roundLower.includes('quarter')) {
      return 'Semi Finals';
    } else if (roundLower.includes('final') && !roundLower.includes('semi') && !roundLower.includes('quarter')) {
      return 'Finals';
    }
    return round.split(/[_\s]+/).map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  // Sort rounds by order (Round of 16 -> Quarter -> Semi -> Finals)
  const getRoundOrder = (roundName: string): number => {
    const roundLower = roundName.toLowerCase();
    if (roundLower.includes('round of 16') || roundLower.includes('round_16')) return 1;
    if (roundLower.includes('quarter')) return 2;
    if (roundLower.includes('semi')) return 3;
    if (roundLower.includes('final') && !roundLower.includes('semi') && !roundLower.includes('quarter')) return 4;
    return 5;
  };

  // Transform API response to component format
  const transformApiResponse = (apiData: any[], isSingles: boolean): KnockoutRound[] => {
    if (!Array.isArray(apiData) || apiData.length === 0) {
      return [];
    }

    const transformed = apiData.map((roundData: any) => {
      const matches: KnockoutMatch[] = (roundData.matches || []).map((match: any) => {
        const team1Info = match.team1Info || {};
        const team2Info = match.team2Info || {};
        
        const isTeam1 = team1Info.name !== undefined;
        const isTeam2 = team2Info.name !== undefined;
        
        let participant1: any = null;
        let participant2: any = null;
        
        if (isSingles) {
          participant1 = team1Info.players?.[0] || null;
          participant2 = team2Info.players?.[0] || null;
        } else {
          if (isTeam1) {
            participant1 = {
              _id: team1Info._id,
              name: team1Info.name,
              shortName: team1Info.shortName,
              logo: team1Info.logo
            };
          } else {
            participant1 = team1Info.players?.[0] || null;
          }
          
          if (isTeam2) {
            participant2 = {
              _id: team2Info._id,
              name: team2Info.name,
              shortName: team2Info.shortName,
              logo: team2Info.logo
            };
          } else {
            participant2 = team2Info.players?.[0] || null;
          }
        }

        const sets = match.sets || [];
        const scores1: number[] = [];
        const scores2: number[] = [];
        
        sets.forEach((set: any) => {
          if (set.side1Points !== undefined && set.side1Points !== null) {
            scores1.push(set.side1Points);
          }
          if (set.side2Points !== undefined && set.side2Points !== null) {
            scores2.push(set.side2Points);
          }
        });

        let winner: any = null;
        if (match.winner) {
          if (participant1?._id === match.winner) {
            winner = {
              _id: participant1._id,
              name: participant1.name,
              type: isSingles ? 'player' : 'team'
            };
          } else if (participant2?._id === match.winner) {
            winner = {
              _id: participant2._id,
              name: participant2.name,
              type: isSingles ? 'player' : 'team'
            };
          } else if (!isSingles) {
            if (team1Info._id === match.winner) {
              winner = {
                _id: team1Info._id,
                name: team1Info.name,
                type: 'team'
              };
            } else if (team2Info._id === match.winner) {
              winner = {
                _id: team2Info._id,
                name: team2Info.name,
                type: 'team'
              };
            }
          }
        }

        const transformedMatch: KnockoutMatch = {
          _id: match._id,
          round: roundData.round || '',
          date: match.date,
          time: match.time,
          status: match.status || 'pending',
          winner: winner,
          scores: isSingles ? {
            player1: scores1,
            player2: scores2
          } : {
            team1: scores1,
            team2: scores2
          }
        };

        if (isSingles) {
          transformedMatch.player1 = participant1;
          transformedMatch.player2 = participant2;
        } else {
          transformedMatch.team1 = participant1;
          transformedMatch.team2 = participant2;
        }

        return transformedMatch;
      });

      const normalizedRoundName = normalizeRoundName(roundData.round || '');
      const orderedMatches = [...matches];
      
      return {
        roundName: normalizedRoundName,
        matches: orderedMatches
      };
    });

    const sorted = transformed.sort((a, b) => {
      const orderA = getRoundOrder(a.roundName);
      const orderB = getRoundOrder(b.roundName);
      return orderA - orderB;
    });

    return sorted;
  };

  const tournamentSeasonId = location.state?.tournamentSeasonId;
  const fromTournamentDetailPage = location.state?.fromTournamentDetailPage || false;

  let currentSport: string | undefined;
  try {
    const selectedTournamentFromState = (location.state as any)?.selectedTournament;
    if (selectedTournamentFromState?.sports) {
      currentSport = selectedTournamentFromState.sports;
    } else {
      currentSport = getSportValueUtil(null, location);
    }
  } catch {
    const urlParams = new URLSearchParams(location.search);
    currentSport = urlParams.get('sport') || 'Badminton';
  }

  const selectedTournament = (location.state as any)?.selectedTournament || (currentSport ? { sports: currentSport } : undefined);

  const isSingles = ageCategoryInfo?.format?.toLowerCase() === 'singles' || 
                    ageCategoryInfo?.format?.toLowerCase() === 'player' ||
                    ageCategoryInfo?.format?.toLowerCase() === 'single';

  const fetchAgeCategoryInfo = useCallback(async () => {
    if (!tournamentId || !ageCategoryId) return;

    try {
      const sport = currentSport || 'Badminton';
      const response = await getAllAgeCategoriesPaginated(
        tournamentId,
        1,
        1000,
        '',
        '',
        '',
        tournamentSeasonId || undefined,
        sport
      );

      const ageCategoriesData = response?.paginatedResults || [];
      const foundAgeCategory = ageCategoriesData.find((ac: any) => ac._id === ageCategoryId);

      if (foundAgeCategory) {
        setAgeCategoryInfo({
          _id: foundAgeCategory._id,
          ageCategory: foundAgeCategory.ageCategory,
          format: foundAgeCategory.format,
          standingsType: foundAgeCategory.standingsType,
        });
      }
    } catch (error) {
      console.error('Error fetching age category info:', error);
    }
  }, [tournamentId, ageCategoryId, tournamentSeasonId, currentSport]);

  const loadKnockouts = useCallback(async () => {
    if (!tournamentId || !ageCategoryId) return;

    try {
      setLoading(true);
      setError(null);

      const sport = currentSport || 'Badminton';
      const response = await getKnockouts(sport, tournamentId, ageCategoryId, tournamentSeasonId);

      if (response && response.data) {
        const apiDataArray = Array.isArray(response.data) ? response.data : (response.data as any).rounds || [];
        
        if (apiDataArray.length === 0) {
          setKnockoutsData(null);
          setError(null);
        } else {
          const currentIsSingles = isSingles;
          const transformedRounds = transformApiResponse(apiDataArray, currentIsSingles);
          setKnockoutsData({ rounds: transformedRounds });
        }
        
        if ((response.data as any).ageCategory) {
          setAgeCategoryInfo((prev: any) => ({
            ...prev,
            format: (response.data as any).ageCategory.format || prev?.format,
          }));
        }
      } else {
        setKnockoutsData(null);
        setError(null);
      }
    } catch (err: any) {
      console.error('Error loading knockouts:', err);
      if (err.response && err.response.status === 404) {
        setKnockoutsData(null);
        setError(null);
      } else if (err.response) {
        setError(`API Error: ${err.response.status} - ${err.response.data?.message || err.response.statusText}`);
      } else if (err.request) {
        setError('Network Error: Unable to reach server');
      } else {
        setError(`Error: ${err.message || 'Failed to load knockouts'}`);
      }
    } finally {
      setLoading(false);
    }
  }, [tournamentId, ageCategoryId, tournamentSeasonId, currentSport, isSingles]);

  useEffect(() => {
    fetchAgeCategoryInfo();
  }, [fetchAgeCategoryInfo]);

  useEffect(() => {
    if (ageCategoryInfo && tournamentId && ageCategoryId) {
      loadKnockouts();
    }
  }, [ageCategoryInfo, tournamentId, ageCategoryId]);

  const handleBack = () => {
    if (fromTournamentDetailPage) {
      navigate(`/manage-tournaments-fixtures/${tournamentId}`, {
        state: {
          activeTab: 'standings',
          selectedTournament,
          tournamentSeasonId,
        },
      });
    } else if (tournamentSeasonId) {
      navigate(`/manage-tournaments-fixtures/${tournamentId}/seasons/${tournamentSeasonId}`, {
        state: {
          activeTab: 'standings',
          selectedTournament,
        },
      });
    } else {
      navigate(`/manage-tournaments-fixtures/${tournamentId}`, {
        state: {
          activeTab: 'standings',
          selectedTournament,
        },
      });
    }
  };

  const getParticipantName = (match: KnockoutMatch, participant: 'player1' | 'player2' | 'team1' | 'team2'): string => {
    if (isSingles) {
      const player = participant === 'player1' ? match.player1 : match.player2;
      return player?.name || 'TBC';
    } else {
      const team = participant === 'team1' ? match.team1 : match.team2;
      return team?.name || 'TBC';
    }
  };

  const getParticipantScores = (match: KnockoutMatch, participant: 'player1' | 'player2' | 'team1' | 'team2'): number[] => {
    if (!match.scores) return [];
    if (isSingles) {
      return participant === 'player1' ? (match.scores.player1 || []) : (match.scores.player2 || []);
    } else {
      return participant === 'team1' ? (match.scores.team1 || []) : (match.scores.team2 || []);
    }
  };

  const isWinner = (match: KnockoutMatch, participant: 'player1' | 'player2' | 'team1' | 'team2'): boolean => {
    if (!match.winner) return false;
    if (isSingles) {
      const player = participant === 'player1' ? match.player1 : match.player2;
      return player?._id === match.winner._id;
    } else {
      const team = participant === 'team1' ? match.team1 : match.team2;
      return team?._id === match.winner._id;
    }
  };

  // Check if match has a "Bye" (only one participant)
  const hasBye = (match: KnockoutMatch): boolean => {
    if (isSingles) {
      return !match.player1 || !match.player2;
    } else {
      return !match.team1 || !match.team2;
    }
  };

  const formatDateTime = (date?: string, time?: string): string => {
    if (!date) return '';
    try {
      const dateObj = new Date(date);
      const formattedDate = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (time) {
        const isTime12Hour = time.includes('AM') || time.includes('PM');
        const formattedTime = isTime12Hour ? time : formatTimeTo12Hour(time);
        return `${formattedDate}, ${formattedTime}`;
      }
      return formattedDate;
    } catch {
      return date;
    }
  };

  const formatTimeTo12Hour = (time: string): string => {
    try {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return time;
    }
  };

  const isTBC = (match: KnockoutMatch): boolean => {
    return match.status === 'TBC' || 
           (!match.player1 && !match.team1) || 
           (!match.player2 && !match.team2) ||
           (getParticipantName(match, isSingles ? 'player1' : 'team1') === 'TBC' ||
            getParticipantName(match, isSingles ? 'player2' : 'team2') === 'TBC');
  };

  const getInitials = (name: string): string => {
    if (!name || name === 'TBC') return '?';
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const renderMatch = (match: KnockoutMatch, index: number, roundIndex: number) => {
    const tbc = isTBC(match);
    const bye = hasBye(match);
    const participant1Name = getParticipantName(match, isSingles ? 'player1' : 'team1');
    const participant2Name = getParticipantName(match, isSingles ? 'player2' : 'team2');
    const participant1Scores = getParticipantScores(match, isSingles ? 'player1' : 'team1');
    const participant2Scores = getParticipantScores(match, isSingles ? 'player2' : 'team2');
    const participant1Winner = isWinner(match, isSingles ? 'player1' : 'team1');
    const participant2Winner = isWinner(match, isSingles ? 'player2' : 'team2');
    const isQualified1 = match.isQualified && participant1Winner;
    const dateTime = formatDateTime(match.date, match.time);

    const matchKey = `${roundIndex}-${index}`;
    return (
      <div 
        key={match._id || index} 
        className="knockout-match" 
        data-match-index={index} 
        data-round-index={roundIndex}
        ref={(el) => { matchRefs.current[matchKey] = el; }}
      >
        <div className="match-box">
          <div className="match-participant">
            <div className="participant-info">
              <div className="participant-avatar">
                {getInitials(participant1Name)}
              </div>
              <span className="participant-name">
                {participant1Name}
                {isQualified1 && <span className="qualified-badge">(Q)</span>}
              </span>
            </div>
            {!tbc && !bye && participant1Scores.length > 0 && (
              <span className={`match-scores ${participant1Winner ? 'winner' : ''}`}>
                {participant1Scores.map((score, idx) => (
                  <span key={idx} className={`score-item ${participant1Winner ? 'bold' : ''}`}>
                    S{idx + 1} {score}
                  </span>
                ))}
              </span>
            )}
            {bye && !participant2Name && (
              <span className="match-scores">Bye</span>
            )}
          </div>
          {!bye && (
            <div className="match-participant">
              <div className="participant-info">
                <div className="participant-avatar">
                  {getInitials(participant2Name)}
                </div>
                <span className="participant-name">
                  {participant2Name}
                  {match.isQualified && participant2Winner && <span className="qualified-badge">(Q)</span>}
                </span>
              </div>
              {!tbc && participant2Scores.length > 0 && (
                <span className={`match-scores ${participant2Winner ? 'winner' : ''}`}>
                  {participant2Scores.map((score, idx) => (
                    <span key={idx} className={`score-item ${participant2Winner ? 'bold' : ''}`}>
                      S{idx + 1} {score}
                    </span>
                  ))}
                </span>
              )}
            </div>
          )}
          {dateTime && (
            <div className="match-datetime">{dateTime}</div>
          )}
        </div>
      </div>
    );
  };

  const getNextRoundMatchIndex = (currentMatchIndex: number): number => {
    return Math.floor(currentMatchIndex / 2);
  };

  /**
   * Calculate separator height between matches (exponential spacing)
   * This ensures proper vertical alignment across rounds
   * Ported from reference implementation
   */
  const calculateSeparatorHeight = (
    roundIndex: number,
    itemsMarginVertical: number,
    cardHeight: number
  ): number => {
    if (roundIndex === 0) return itemsMarginVertical;
    
    // Exponential spacing: 2^roundIndex - 1
    const matchSpacing = Math.pow(2, roundIndex) - 1;
    return cardHeight * matchSpacing + itemsMarginVertical * (matchSpacing + 1);
  };

  /**
   * Calculate match Y position with vertical centering
   * This ensures all rounds are properly aligned
   * Ported from reference implementation
   */
  const getMatchYPositionCalculated = (
    roundIndex: number,
    matchIndex: number,
    rounds: KnockoutRound[],
    cardHeight: number,
    itemsMarginVertical: number
  ): number => {
    const matchCount = rounds[roundIndex].matches.length;
    
    const separatorHeight = calculateSeparatorHeight(
      roundIndex,
      itemsMarginVertical,
      cardHeight
    );
    
    const totalColumnHeight =
      rounds[0].matches.length * cardHeight +
      (rounds[0].matches.length - 1) * itemsMarginVertical +
      60;
    
    const roundContentHeight =
      matchCount * cardHeight +
      (matchCount - 1) * separatorHeight;
    
    const topOffset = (totalColumnHeight - roundContentHeight) / 2;
    
    return (
      topOffset +
      matchIndex * (cardHeight + separatorHeight) +
      cardHeight / 2 +
      12
    );
  };

  /**
   * Calculate match position and participant row positions
   * Uses calculated positions based on exponential spacing (matching reference implementation)
   * Falls back to DOM measurements if available for fine-tuning
   */
  const getMatchPosition = useCallback((roundIdx: number, matchIdx: number, rounds: KnockoutRound[]) => {
    const matchKey = `${roundIdx}-${matchIdx}`;
    const matchElement = matchRefs.current[matchKey];
    
    // Calculate base position using reference implementation logic
    const separatorHeight = calculateSeparatorHeight(
      roundIdx,
      ITEMS_MARGIN_VERTICAL,
      CARD_HEIGHT
    );
    
    const totalColumnHeight =
      rounds[0].matches.length * CARD_HEIGHT +
      (rounds[0].matches.length - 1) * ITEMS_MARGIN_VERTICAL +
      60;
    
    const roundContentHeight =
      rounds[roundIdx].matches.length * CARD_HEIGHT +
      (rounds[roundIdx].matches.length - 1) * separatorHeight;
    
    const topOffset = (totalColumnHeight - roundContentHeight) / 2;
    
    const matchTop = topOffset + matchIdx * (CARD_HEIGHT + separatorHeight);
    const matchHeight = CARD_HEIGHT;
    
    if (matchElement) {
      const roundMatchesContainer = matchElement.closest('.round-matches');
      if (roundMatchesContainer) {
        const matchRect = matchElement.getBoundingClientRect();
        const containerRect = roundMatchesContainer.getBoundingClientRect();
        const relativeTop = matchRect.top - containerRect.top;
        const actualMatchHeight = matchRect.height;
        
        const matchBoxPadding = 14;
        const participantRowPadding = 8;
        const participantRowHeight = 40;
        const participantRowTotalHeight = participantRowHeight + participantRowPadding * 2;
        const participantMargin = 10;
        
        return {
          top: relativeTop,
          center: relativeTop + actualMatchHeight / 2,
          height: actualMatchHeight,
          participant1Y: relativeTop + matchBoxPadding + participantRowPadding + (participantRowHeight / 2),
          participant2Y: relativeTop + matchBoxPadding + participantRowTotalHeight + participantMargin + participantRowPadding + (participantRowHeight / 2)
        };
      }
    }
    
    const matchBoxPadding = 14;
    const participantRowPadding = 8;
    const participantRowHeight = 40;
    const participantRowTotalHeight = participantRowHeight + participantRowPadding * 2;
    const participantMargin = 10;
    
    const participant1Y = matchTop + matchBoxPadding + participantRowPadding + (participantRowHeight / 2);
    const participant2Y = matchTop + matchBoxPadding + participantRowTotalHeight + participantMargin + participantRowPadding + (participantRowHeight / 2);
    
    return {
      top: matchTop,
      center: matchTop + matchHeight / 2,
      height: matchHeight,
      participant1Y: participant1Y,
      participant2Y: participant2Y
    };
  }, []);

  /**
   * Render connecting lines between rounds using SVG with proper Y-shaped brackets
   * This follows library-quality patterns for tournament bracket rendering
   */
  const renderConnectingLines = (rounds: KnockoutRound[], roundIndex: number) => {
    if (roundIndex >= rounds.length - 1) return null;

    const currentRound = rounds[roundIndex];
    const nextRound = rounds[roundIndex + 1];
    
    const matchGap = 20;
    const connectorWidth = 80;
    const horizontalOffset = 30;
    
    const calculateTotalHeight = () => {
      const currentRoundHeight = currentRound.matches.length > 0 
        ? (getMatchPosition(roundIndex, currentRound.matches.length - 1, rounds).top + getMatchPosition(roundIndex, currentRound.matches.length - 1, rounds).height)
        : 0;
      const nextRoundHeight = nextRound.matches.length > 0
        ? (getMatchPosition(roundIndex + 1, nextRound.matches.length - 1, rounds).top + getMatchPosition(roundIndex + 1, nextRound.matches.length - 1, rounds).height)
        : 0;
      return Math.max(currentRoundHeight, nextRoundHeight);
    };
    
    const totalHeight = calculateTotalHeight();
    
    /**
     * Group matches into pairs for bracket connections
     * Standard knockout format: 
     * - Match 0 and Match 1 -> Next Round Match 0
     * - Match 2 and Match 3 -> Next Round Match 1
     * - Match 4 and Match 5 -> Next Round Match 2
     */
    const createMatchPairs = (): number[][] => {
      const pairs: number[][] = [];
      for (let i = 0; i < currentRound.matches.length; i += 2) {
        if (i + 1 < currentRound.matches.length) {
          pairs.push([i, i + 1]);
        } else {
          pairs.push([i]);
        }
      }
      return pairs;
    };
    
    const matchPairs = createMatchPairs();
    const mergeX = connectorWidth / 2;
    
    /**
     * Render a single Y-shaped connector for a match pair
     * Standard tournament bracket pattern: Two matches → One vertical stem → Two participant slots
     * FIXED: Draw as single continuous paths to avoid duplicate lines
     */
    const renderYConnector = (pair: number[], pairIndex: number): JSX.Element[] => {
      const nextMatchIndex = pairIndex;
      if (nextMatchIndex >= nextRound.matches.length) return [];
      
      const paths: JSX.Element[] = [];
      
      const nextMatchPos = getMatchPosition(roundIndex + 1, nextMatchIndex, rounds);
      const mergeY = nextMatchPos.center;
      const topParticipantY = nextMatchPos.participant1Y;
      const bottomParticipantY = nextMatchPos.participant2Y;
      
      const adjustedMergeY = Math.max(Math.min(mergeY, bottomParticipantY), topParticipantY);
      
      if (pair.length >= 1) {
        const firstMatchIndex = pair[0];
        const firstMatchPos = getMatchPosition(roundIndex, firstMatchIndex, rounds);
        const firstMatchCenter = firstMatchPos.center;
        
        paths.push(
          <path
            key={`path-first-${roundIndex}-${firstMatchIndex}-to-${nextMatchIndex}`}
            d={`M 0 ${firstMatchCenter} 
                L ${mergeX - horizontalOffset} ${firstMatchCenter} 
                L ${mergeX - horizontalOffset} ${adjustedMergeY}`}
            stroke="#4a90e2"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        );
      }
      
      if (pair.length >= 2) {
        const secondMatchIndex = pair[1];
        const secondMatchPos = getMatchPosition(roundIndex, secondMatchIndex, rounds);
        const secondMatchCenter = secondMatchPos.center;
        
        paths.push(
          <path
            key={`path-second-${roundIndex}-${secondMatchIndex}-to-${nextMatchIndex}`}
            d={`M 0 ${secondMatchCenter} 
                L ${mergeX - horizontalOffset} ${secondMatchCenter} 
                L ${mergeX - horizontalOffset} ${adjustedMergeY}`}
            stroke="#4a90e2"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        );
        
        paths.push(
          <path
            key={`path-single-entry-${roundIndex}-${pairIndex}`}
            d={`M ${mergeX - horizontalOffset} ${adjustedMergeY} 
                L ${mergeX} ${adjustedMergeY} 
                L ${connectorWidth} ${adjustedMergeY}`}
            stroke="#4a90e2"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        );
      } else if (pair.length === 1) {
        paths.push(
          <path
            key={`path-single-stem-${roundIndex}-${pairIndex}`}
            d={`M ${mergeX} ${adjustedMergeY} 
                L ${mergeX} ${topParticipantY} 
                L ${connectorWidth} ${topParticipantY}`}
            stroke="#4a90e2"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        );
      }
      
      return paths;
    };
    
    const allPaths: JSX.Element[] = [];
    matchPairs.forEach((pair, pairIndex) => {
      const connectorPaths = renderYConnector(pair, pairIndex);
      allPaths.push(...connectorPaths);
    });
    
    if (allPaths.length === 0) return null;
    
    return (
      <div className="round-connectors" style={{ height: `${totalHeight}px` }}>
        <svg 
          width={connectorWidth} 
          height={totalHeight} 
          style={{ position: 'absolute', top: 0, left: 0 }}
          viewBox={`0 0 ${connectorWidth} ${totalHeight}`}
          preserveAspectRatio="none"
        >
          {allPaths}
        </svg>
      </div>
    );
  };

  const CARD_HEIGHT = 180;
  const CARD_WIDTH = 240;
  const ITEMS_MARGIN_VERTICAL = 20;
  const LINE_WIDTH = 80;

  const renderRound = (round: KnockoutRound, roundIndex: number, allRounds: KnockoutRound[]) => {
    const separatorHeight = calculateSeparatorHeight(
      roundIndex,
      ITEMS_MARGIN_VERTICAL,
      CARD_HEIGHT
    );

    const totalColumnHeight =
      allRounds[0].matches.length * CARD_HEIGHT +
      (allRounds[0].matches.length - 1) * ITEMS_MARGIN_VERTICAL +
      60;

    const roundContentHeight =
      round.matches.length * CARD_HEIGHT +
      (round.matches.length - 1) * separatorHeight;

    const topOffset = (totalColumnHeight - roundContentHeight) / 2;

    return (
      <div key={round.roundName} className="knockout-round">
        <div className="round-header">
          <span className="round-label">{round.roundName}</span>
        </div>
        <div 
          className="round-matches" 
          style={{ 
            position: 'relative',
            minHeight: `${totalColumnHeight}px`,
            paddingTop: `${topOffset}px`
          }}
        >
          {round.matches && round.matches.length > 0 ? (
            round.matches.map((match, index) => {
              const matchKey = `${roundIndex}-${index}`;
              const marginBottom = index < round.matches.length - 1 ? separatorHeight : 0;
              
              return (
                <div 
                  key={match._id || index}
                  style={{ marginBottom: `${marginBottom}px` }}
                >
                  {renderMatch(match, index, roundIndex)}
                </div>
              );
            })
          ) : (
            <div className="no-matches">No matches</div>
          )}
          {renderConnectingLines(allRounds, roundIndex)}
        </div>
      </div>
    );
  };

  const breadcrumbItems = [
    { 
      label: 'Manage Tournaments', 
      onClick: () => navigate('/manage-tournaments-fixtures') 
    },
    { 
      label: 'Tournament Details', 
      onClick: () => {
        const navigationState: any = {
          activeTab: 'standings',
          selectedTournament,
          tournamentSeasonId,
        };
        
        if (fromTournamentDetailPage || !tournamentSeasonId) {
          navigate(`/manage-tournaments-fixtures/${tournamentId}`, { 
            state: navigationState
          });
        } else {
          navigate(`/manage-tournaments-fixtures/${tournamentId}/seasons/${tournamentSeasonId}`, { 
            state: navigationState
          });
        }
      }
    },
    { 
      label: `${ageCategoryInfo?.ageCategory || 'Age Category'} - Knockouts`, 
      active: true 
    },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>

        <div style={{ padding: '0 15px' }}>
          <div className="page-header-section mb-4" style={{ position: 'relative', zIndex: 10 }}>
            <div className="d-flex align-items-center mb-4" style={{ gap: '16px', position: 'relative', zIndex: 10, marginBottom: '24px !important', justifyContent: 'flex-start', width: '100%' }}>
              <button 
                className="back-button"
                onClick={handleBack}
                style={{ position: 'relative', zIndex: 10 }}
              >
                <i className="ri-arrow-left-line"></i>
              </button>
              <div className="tournament-logo-small" style={{ flexShrink: 0, position: 'relative', zIndex: 10 }}>
                <div className="default-tournament-logo-small">
                  <i className="ri-trophy-line"></i>
                </div>
              </div>
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <h1 className="page-title mb-0" style={{ position: 'relative', zIndex: 10 }}>
                  {ageCategoryInfo?.ageCategory || 'Age Category'} - Knockouts
                </h1>
                <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                  <span className="tournament-short-name-badge">Knockouts</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ padding: '0 15px' }}>
          {loading ? (
            <Card>
              <Card.Body>
                <ShimmerLoader type="table" rows={5} />
              </Card.Body>
            </Card>
          ) : error ? (
            <Card>
              <Card.Body>
                <div className="alert alert-danger">{error}</div>
                <Button variant="primary" onClick={loadKnockouts}>
                  Retry
                </Button>
              </Card.Body>
            </Card>
          ) : knockoutsData && knockoutsData.rounds && knockoutsData.rounds.length > 0 ? (
            <Card style={{ border: 'none', boxShadow: 'none' }}>
              <Card.Body style={{ padding: '0' }}>
                <div className="knockouts-bracket">
                  {knockoutsData.rounds.map((round, index) => {
                    return renderRound(round, index, knockoutsData.rounds);
                  })}
                </div>
              </Card.Body>
            </Card>
          ) : (
            <Card>
              <Card.Body>
                <div className="text-center py-5">
                  <i className="ri-trophy-line text-muted" style={{ fontSize: '3rem' }}></i>
                  <p className="text-muted mt-3">No knockouts found</p>
                  <p className="text-muted small">Knockouts will appear here once matches are scheduled.</p>
                </div>
              </Card.Body>
            </Card>
          )}
        </div>
      </Container>
    </div>
  );
};

export default KnockoutsDetailPage;
